import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { Login, ContentCopy } from '@mui/icons-material';

import signalsLogo from '../../assets/icons/signals-logo-blue.svg';

import AuthLayout from './shared/AuthLayout';
import AuthHeader from './shared/AuthHeader';
import AuthCard from './shared/AuthCard';
import { AUTH_COLORS, AUTH_BREAKPOINTS } from './shared/authConstants';

const SentinelSignals = ({ onBack }) => {
  const [emailCopied, setEmailCopied] = useState(false);
  const isMobile = useMediaQuery(AUTH_BREAKPOINTS.mobile);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('inquiries@mqcapital.co.uk');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = 'inquiries@mqcapital.co.uk';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    }
  };

  const renderCredentialsCard = () => (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}
      >
        <AuthHeader
          icon={signalsLogo}
          title={null}
          subtitle='Need Access Credentials?'
          description='Contact our team to get access to Signals'
          iconColor={AUTH_COLORS.SIGNAL_PURPLE}
        />
      </Box>

      <Typography
        variant='body1'
        sx={{
          color: '#555',
          mb: 3,
          lineHeight: 1.6,
          fontSize: isMobile ? '1rem' : '1.1rem'
        }}
      >
        If you don't have login credentials for Signals, please contact our team
        and we'll provide you with access details.
      </Typography>

      <Box
        sx={{
          backgroundColor: 'rgba(156, 39, 176, 0.1)',
          borderRadius: 2,
          p: 3,
          mb: 3,
          border: '1px solid rgba(156, 39, 176, 0.2)',
          textAlign: 'center'
        }}
      >
        <Typography
          variant='body2'
          sx={{
            color: '#666',
            fontWeight: 500,
            mb: 1
          }}
        >
          Contact us at:
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2
          }}
        >
          <Typography
            variant='h6'
            sx={{
              color: AUTH_COLORS.SIGNAL_PURPLE,
              fontWeight: 600,
              fontFamily: 'monospace',
              fontSize: isMobile ? '1rem' : '1.25rem'
            }}
          >
            inquiries@mqcapital.co.uk
          </Typography>

          <IconButton
            onClick={handleCopyEmail}
            size='small'
            sx={{
              color: emailCopied ? '#4caf50' : AUTH_COLORS.SIGNAL_PURPLE,
              '&:hover': {
                backgroundColor: 'rgba(156, 39, 176, 0.1)'
              }
            }}
          >
            <ContentCopy fontSize='small' />
          </IconButton>
        </Box>

        {emailCopied && (
          <Typography
            variant='caption'
            sx={{
              color: '#4caf50',
              fontWeight: 500,
              display: 'block',
              mb: 1
            }}
          >
            ✓ Email copied to clipboard!
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography
          variant='body2'
          sx={{
            color: '#666',
            fontStyle: 'italic',
            mb: 1
          }}
        >
          Please write your own email to request access
        </Typography>

        <Typography
          variant='caption'
          sx={{
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          Our team typically responds within 24 hours
        </Typography>
      </Box>

      <Box
        sx={{
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          pt: 2,
          textAlign: 'center'
        }}
      >
        <Typography
          variant='body2'
          sx={{
            color: '#666',
            mb: 1,
            fontSize: '0.875rem'
          }}
        >
          Already have credentials?
        </Typography>
        <Button
          variant='text'
          startIcon={<Login />}
          onClick={() =>
            window.open('https://sentinel.mqcapital.co.uk/', '_blank')
          }
          sx={{
            color: AUTH_COLORS.SIGNAL_PURPLE,
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(156, 39, 176, 0.08)'
            }
          }}
        >
          Sign In
        </Button>
      </Box>
    </Box>
  );

  return (
    <AuthLayout onBack={onBack}>
      <AuthCard>{renderCredentialsCard()}</AuthCard>
    </AuthLayout>
  );
};

export default SentinelSignals;
